import { openExternalUrl } from "./openExternalUrl";
import { resolveEditInBrowserUrl } from "./resolveEditInBrowserUrl";
import {
  openInOfficeDesktop,
  resolveOfficeDesktopUrl,
  buildOfficeDesktopUri,
} from "./openInDesktopApp";
import { getOfficeDesktopAppKind } from "../functions/helperMethod";
import { logger } from "./logger";

/**
 * Opens a document in its Office desktop app when possible, otherwise the browser.
 *
 * For an Office file (Word/Excel/PowerPoint) with a resolvable direct path, this
 * launches the desktop app and falls back to opening in the browser when the app
 * isn't available (not installed / launch blocked). Anything else - a non-Office
 * file, or a file we can't resolve a direct path for - opens in the browser.
 *
 * @param {object | null | undefined} file
 *   Document-like object. Reads `webDavUrl` (direct path, preferred) and
 *   `webUrl`/`WebUrl` (browser/viewer link). `FileType`/`name` help classify the
 *   Office app; when absent the file extension of the direct path is used.
 * @param {{ logLabel?: string }} [options] logLabel tags the diagnostic log entry.
 */
export function openDocumentPreferDesktop(file, { logLabel = "openDocumentPreferDesktop" } = {}) {
  const browserUrl = file?.webUrl ?? file?.WebUrl;
  const openInBrowser = () => {
    if (browserUrl) {
      openExternalUrl(resolveEditInBrowserUrl(String(browserUrl)));
    }
  };

  const kind = getOfficeDesktopAppKind(
    file?.FileType ?? file?.name ?? file?.webDavUrl ?? browserUrl,
    file,
  );
  const desktopUrl = kind ? resolveOfficeDesktopUrl(file) : "";

  logger.info(
    logLabel,
    {
      fileId: file?.id,
      fileName: file?.name,
      appKind: kind,
      webUrl: browserUrl,
      webDavUrl: file?.webDavUrl,
      resolvedUrl: desktopUrl,
      uri: buildOfficeDesktopUri(kind, desktopUrl),
      target: kind && desktopUrl ? "desktop-then-browser" : "browser",
    },
    "DESKTOP",
  );

  if (kind && desktopUrl) {
    openInOfficeDesktop(kind, desktopUrl, { onUnavailable: openInBrowser });
    return;
  }

  openInBrowser();
}
